import type { LogItem } from "ctxReflector/components/L3overview/LogsData";
import { splitMsgId } from "ctxReflector/components/L3overview/logMessage";

/**
 * One log line as the shared table and `logGrouping` see it, whichever
 * Reflector it came from. `raw` is the page's own row (`LogItem` for V1,
 * `LogEntry` for V2), handed back untouched to expanded rows, row clicks and
 * the extra columns, so none of them has to re-derive it.
 */
export interface ReflectorLogRecord<TRaw> {
  /** Stable across re-renders and loads of further pages: expanded rows follow it. */
  key: string;
  /** Local time, `yyyy-MM-dd HH:mm:ss[.ffffff]`, so it also sorts chronologically. */
  timestamp: string;
  level: string;
  message: string;
  /** Leading MSGID of the message, when it has one. */
  msgid: string | null;
  raw: TRaw;
}

/**
 * `occurrence` is the 0-based count of earlier lines in the loaded list with the
 * same `rawTimestamp`: the handler gives no id, and two lines can share a
 * microsecond, so the timestamp alone is not a key.
 */
export function fromV1(item: LogItem, occurrence: number): ReflectorLogRecord<LogItem> {
  return {
    key: `${item.rawTimestamp}#${occurrence}`,
    timestamp: item.timestamp,
    level: item.level,
    message: item.message,
    msgid: splitMsgId(item.message).msgId,
    raw: item,
  };
}

/** `fromV1` over the whole loaded list, numbering repeated timestamps. */
export function fromV1List(items: LogItem[]): ReflectorLogRecord<LogItem>[] {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const occurrence = seen.get(item.rawTimestamp) ?? 0;
    seen.set(item.rawTimestamp, occurrence + 1);
    return fromV1(item, occurrence);
  });
}
